import { FormattedMessage, useIntl } from "react-intl";
import { InlineEdit } from "@/components/design/InlineEdit";
import { Select } from "@/components/design/Select";
import { DegreeSelect } from "@/components/design/DegreeSelect";
import { MonthYearSelect } from "@/components/design/MonthYearSelect";
import type { FieldOption } from "@/lib/field-util";
import { getLanguageOptions, isKnownLanguageTag } from "@/lib/language-options";
import { useUiStore } from "@/stores/ui-store";
import { isBeforeBirthDate, isStartAfterEnd } from "@/lib/date-instant";
import type { CollectionKey, MergeSide, ResolutionChoice, RowDiff, RowFieldDiff } from "./profile-merge-types";
import { fieldLabel, languageDisplayName } from "./profile-merge-labels";
import { MergeGuideControls } from "@/components/save/ProfileMergeGuide";
import { MergeDateWarning } from "@/components/save/MergeDateWarning";
import { MergeFieldWarning } from "@/components/save/MergeFieldWarning";
import { rowFieldError, rowFieldMaxChars } from "@/lib/import-field-validation";
import { useDateFieldWarning } from "@/components/save/use-date-field-warning";
import { useFieldWarning } from "@/components/save/use-field-warning";
import { useGuideCard } from "@/components/save/use-guide-card";
import {
  useProfileMergeStore,
  committedDateOfBirth,
  defaultChoice,
  languageNeedsChoice,
  rowTakeBlockedByUnknownLanguage,
  rowFieldExistingPristine,
  rowFieldPristine,
  rowReplacesSaved,
} from "@/stores/profile-merge-store";

// In-line LinkedIn merge for a collection section (experience, education,
// languages, certificates). Rendered inside the matching profile Section: it
// lists the incoming rows genesis found — a brand-new entry (ADD) or a matched
// entry whose details differ (CONFLICT) — as neutral cards. Each card carries
// the row's structured per-field diff. On a CONFLICT both sides of every
// EDITABLE field are editable in place (mirroring the person_detail pair):
// editing the incoming side selects the row for import (USE_INCOMING), editing
// the existing side keeps + partially updates the saved entry (KEEP_EXISTING).
// Non-editable fields (a matched language tag) render read-only — except a
// language the import could not resolve to a tag, which becomes a chooser so the
// user can correct or skip it (see languageNeedsChoice).
//
// The keep/use decision runs through the guided review (ProfileMergeGuide):
// the active card carries the decision buttons, every other card a status line
// with a Review button. Choices/edits are recorded in the merge store only;
// the commit is driven by the bar. Renders nothing when there is no active
// preview or the section has no actionable (non-UNCHANGED) rows, so it is safe
// to drop into every section unconditionally. (Skills/expertises use
// ProfileMergeSkills instead.)

type Intl = ReturnType<typeof useIntl>;

// CEFR grades render verbatim (A1..C2 are the same in every locale); only the
// NATIVE rung is a localizable word.
const CEFR_VALUES = ["A1", "A2", "B1", "B2", "C1", "C2", "NATIVE"] as const;

function cefrOptions(intl: Intl): FieldOption[] {
  return CEFR_VALUES.map((value) => ({
    value,
    label:
      value === "NATIVE"
        ? intl.formatMessage({ id: "profile.merge.cefr.native", defaultMessage: "Native" })
        : value,
  }));
}

// A field's stored machine value rendered for read-only display: a language tag
// as its full name, a CEFR NATIVE rung as its localized word (grades verbatim),
// everything else as-is. null for a blank value (the caller shows a dash).
function machineDisplay(
  field: RowFieldDiff,
  raw: string | null,
  intl: Intl,
  degreeOptions: readonly FieldOption[],
): string | null {
  if (raw == null || raw === "") return null;
  if (field.field === "language") return languageDisplayName(raw, intl.locale);
  if (field.field === "degree") return degreeOptions.find((option) => option.value === raw)?.label ?? raw;
  if (field.kind === "CEFR") {
    return raw === "NATIVE"
      ? intl.formatMessage({ id: "profile.merge.cefr.native", defaultMessage: "Native" })
      : raw;
  }
  return raw;
}

function ResetButton({ onClick, anchorId, label }: { onClick: () => void; anchorId: string; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      id={anchorId}
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-ink-muted transition hover:bg-ink/10 hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink-ui"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-3 w-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v5h5" />
      </svg>
    </button>
  );
}

// An editable value of one row field, for either side of a CONFLICT (the
// incoming LinkedIn value or the user's existing/saved value) — mirroring the
// person_detail compared pair. Editing the INCOMING side selects the row
// (USE_INCOMING); editing the EXISTING side keeps it (KEEP_EXISTING), committed
// as a partial update of the saved entry. A blank keystroke on a TEXT/start
// field reads as rejecting it and does NOT flip the choice (blur snaps it back
// to pristine); a blank endDate is a real value ("present"/ongoing) so it sticks.
function RowFieldEditor({
  row,
  section,
  field,
  fieldId,
  side,
  committed,
  degreeOptions,
}: {
  row: RowDiff;
  section: CollectionKey;
  field: RowFieldDiff;
  fieldId: string;
  side: MergeSide;
  // Whether the current choice would COMMIT this side's value — gates the date
  // warning so it never nags on the discarded side or an untouched saved value.
  committed: boolean;
  degreeOptions: readonly FieldOption[];
}) {
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const rowKey = row.rowKey;
  const existing = side === "existing";
  // Only the INCOMING value is correctable; derived here rather than passed in, so a
  // future callsite can't forget the flag and silently get the read-only path.
  const languageChoice = !existing && languageNeedsChoice(field);
  const edited = useProfileMergeStore((s) => (existing ? s.rowExistingEdits : s.rowEdits)[rowKey]?.[field.field]);
  const setRowEdit = useProfileMergeStore((s) => s.setRowEdit);
  const clearRowEdit = useProfileMergeStore((s) => s.clearRowEdit);
  const setRowExistingEdit = useProfileMergeStore((s) => s.setRowExistingEdit);
  const clearRowExistingEdit = useProfileMergeStore((s) => s.clearRowExistingEdit);
  const setChoice = useProfileMergeStore((s) => s.setChoice);
  // For the endDate editor's start≤end check: the sibling startDate edit on the
  // SAME side, plus the reveal flag that surfaces the range error on a blocked save.
  const startEdited = useProfileMergeStore((s) => (existing ? s.rowExistingEdits : s.rowEdits)[rowKey]?.startDate);
  const revealDateErrors = useProfileMergeStore((s) => s.revealDateErrors);
  // The DOB the commit would leave on the profile — an anchor date (start/issued)
  // before it is a birth-coherence error.
  const committedDob = useProfileMergeStore((s) => committedDateOfBirth(s.preview, s.choices, s.edits));

  const setEdit = existing ? setRowExistingEdit : setRowEdit;
  const clearEdit = existing ? clearRowExistingEdit : clearRowEdit;
  const editChoice: ResolutionChoice = existing ? "KEEP_EXISTING" : "USE_INCOMING";
  const pristine = existing ? rowFieldExistingPristine(row, field.field) : rowFieldPristine(row, field.field);
  const value = edited ?? pristine;
  const dirty = edited !== undefined && edited !== pristine;
  const committedStart =
    startEdited ?? (existing ? rowFieldExistingPristine(row, "startDate") : rowFieldPristine(row, "startDate"));
  // Validate a MONTH date field only on the side that would be committed (and,
  // for the kept side, only once the user has edited it — an untouched saved
  // date isn't re-sent). Mirrors the store's hasInvalidDates gate.
  const validateDate = field.kind === "MONTH" && committed && (side === "incoming" || dirty);
  const dateWarn = useDateFieldWarning(value, validateDate);
  // Same timing as every other warning in the review: on blur, or when a blocked save reveals all of
  // them — never while the user is still typing. `dirty` alone would fire at character 129.
  const lengthWarn = useFieldWarning(
    committed ? rowFieldError(section, field.field, value) : null,
    revealDateErrors,
  );
  const reset = () => clearEdit(rowKey, field.field);
  const resetAnchorId = existing
    ? `profile-merge-row-reset-existing-${rowKey}-${field.field}`
    : `profile-merge-row-reset-${rowKey}-${field.field}`;
  // Reset reverts to the side's pristine value — the saved value for the existing
  // side, the LinkedIn value for the incoming side.
  const resetLabel = intl.formatMessage(
    existing
      ? { id: "profile.merge.reset.existing", defaultMessage: "Reset to your saved value" }
      : { id: "profile.merge.reset.incoming", defaultMessage: "Reset to the imported value" },
  );
  // The incoming editor takes its accessible name from the field's visible
  // <label htmlFor>; the existing editor has no such label, so name it explicitly,
  // qualified by the field so a card's several existing editors stay distinct.
  const ariaLabel = existing
    ? `${fieldLabel(intl, field.field)} — ${intl.formatMessage({ id: "profile.merge.editAria.existing", defaultMessage: "Edit your value" })}`
    : undefined;

  if (languageChoice) {
    // The unrecognised text is never prefilled — it is not one of the options, and
    // the note below quotes it so the user still sees what the document said.
    const picked = edited ?? "";
    const onPick = (next: string) => {
      if (next === "") clearEdit(rowKey, field.field);
      else setEdit(rowKey, field.field, next);
      setChoice(rowKey, editChoice);
    };
    return (
      <div>
        <Select
          id={fieldId}
          value={picked}
          onChange={onPick}
          options={getLanguageOptions(locale)}
          placeholder={intl.formatMessage({
            id: "profile.page.field.language.placeholder",
            defaultMessage: "Choose language",
          })}
          variant="plain"
          className="min-w-0"
        />
        {isKnownLanguageTag(picked) ? null : (
          <div id={`${fieldId}-language-unknown`} className="smycles-field-meta mt-0.5 text-ink-muted">
            <FormattedMessage
              id="profile.merge.language.unknown"
              defaultMessage="“{value}” is not a recognizable language. Choose the right one, or skip this entry."
              values={{ value: pristine }}
            />
          </div>
        )}
      </div>
    );
  }

  if (field.field === "degree") {
    const onSelect = (next: string) => {
      if (next === pristine) clearEdit(rowKey, field.field);
      else setEdit(rowKey, field.field, next);
      setChoice(rowKey, editChoice);
    };
    return (
      <div className="flex items-center gap-1.5">
        {existing ? (
          <label htmlFor={fieldId} className="sr-only">
            {ariaLabel}
          </label>
        ) : null}
        <DegreeSelect
          id={fieldId}
          value={value}
          onChange={onSelect}
          options={degreeOptions}
          placeholder={intl.formatMessage({
            id: "profile.page.field.degree.placeholder",
            defaultMessage: "Choose degree",
          })}
          variant="plain"
          className="min-w-0"
        />
        {dirty ? <ResetButton onClick={reset} anchorId={resetAnchorId} label={resetLabel} /> : null}
      </div>
    );
  }

  if (field.kind === "CEFR") {
    const onSelect = (next: string) => {
      if (next === pristine) clearEdit(rowKey, field.field);
      else setEdit(rowKey, field.field, next);
      setChoice(rowKey, editChoice);
    };
    return (
      <div className="flex items-center gap-1.5">
        {existing ? (
          <label htmlFor={fieldId} className="sr-only">
            {ariaLabel}
          </label>
        ) : null}
        <Select
          id={fieldId}
          value={value}
          onChange={onSelect}
          options={cefrOptions(intl)}
          variant="plain"
          className="min-w-0"
        />
        {dirty ? <ResetButton onClick={reset} anchorId={resetAnchorId} label={resetLabel} /> : null}
      </div>
    );
  }

  if (field.kind === "MONTH") {
    const isEnd = field.field === "endDate";
    const onMonthChange = (next: string) => {
      dateWarn.onEdit();
      lengthWarn.onEdit();
      // A blank non-end date reads as rejecting it (revert to pristine, like the
      // text path's blank-drop); a blank endDate is "present"/ongoing — a real value.
      if ((!isEnd && next.trim() === "") || next === pristine) {
        clearEdit(rowKey, field.field);
      } else {
        setEdit(rowKey, field.field, next);
        /* v8 ignore next -- unreachable: reaching this else already means NOT(!isEnd && blank), which is exactly this condition, so it is always true here */
        if (isEnd || next.trim() !== "") setChoice(rowKey, editChoice);
      }
      // A select change is a discrete commit — surface a shape error (e.g. the
      // "-MM" month-without-year sentinel) at once, not only on a blocked save.
      dateWarn.onBlur();
      lengthWarn.onBlur();
    };
    // Cross-date checks, shown once the user has touched a side or a blocked save
    // revealed errors: start≤end on the endDate, and (start/issued) ≥ birth on the
    // anchor date. Both surface via MergeDateWarning under the field.
    const isAnchor = field.field === "startDate" || field.field === "issued";
    const rangeError = isEnd && committed && isStartAfterEnd(committedStart, value) ? "range" : null;
    const birthError = isAnchor && committed && isBeforeBirthDate(value, committedDob) ? "beforeBirth" : null;
    const gatedExtra = dirty || startEdited !== undefined || revealDateErrors ? (rangeError ?? birthError) : null;
    const shownError = dateWarn.error ?? gatedExtra;
    return (
      <div>
        <div className="flex items-start gap-1.5">
          <MonthYearSelect id={fieldId} value={value} onChange={onMonthChange} className="min-w-0" />
          {dirty ? <ResetButton onClick={reset} anchorId={resetAnchorId} label={resetLabel} /> : null}
        </div>
        <MergeDateWarning id={`${fieldId}-date-warning`} error={shownError} />
      </div>
    );
  }

  const isEndDate = field.field === "endDate";
  const multiline = field.field === "description";
  const onChange = (next: string) => {
    // InlineEdit's Escape-revert fires onChange with the unchanged value — don't
    // let that record an edit or flip the choice.
    if (next === value) return;
    dateWarn.onEdit();
    lengthWarn.onEdit();
    setEdit(rowKey, field.field, next);
    if (isEndDate || next.trim() !== "") setChoice(rowKey, editChoice);
  };
  const onCommit = (committedValue: string) => {
    dateWarn.onBlur();
    lengthWarn.onBlur();
    const blankDrop = !isEndDate && committedValue.trim() === "";
    const pristineEqual = isEndDate ? committedValue === pristine : committedValue.trim() === pristine;
    if (blankDrop || pristineEqual) reset();
  };

  // Date fields (kind === "MONTH") render via MonthYearSelect above; only TEXT
  // fields reach here, so no date placeholder is needed.
  return (
    <div>
      <div className="flex items-start gap-1.5">
        <InlineEdit
          id={fieldId}
          value={value}
          onChange={onChange}
          onCommit={onCommit}
          multiline={multiline}
          wrap={!multiline}
          clearable={false}
          ariaLabel={ariaLabel}
          className="smycles-field-value min-w-0"
          inputClassName="smycles-field-value"
        />
        {dirty ? <ResetButton onClick={reset} anchorId={resetAnchorId} label={resetLabel} /> : null}
      </div>
      <MergeDateWarning id={`${fieldId}-date-warning`} error={dateWarn.error} />
      {/* Length cap for the row's free-text fields — the editor gained these first, so without the
          same note here the review would silently accept a value the editor refuses. Gated the same
          way the date warnings are: only on the side that would be committed. */}
      <MergeFieldWarning
        id={`${fieldId}-field-warning`}
        fieldKey={field.field}
        max={rowFieldMaxChars(section, field.field)}
        error={lengthWarn.error}
      />
    </div>
  );
}

// One labeled field row inside a card: the field's label, the existing value it
// replaces (read-only, on a CONFLICT), and the incoming value (editable, unless
// genesis marked the field non-editable).
function RowFieldRow({
  row,
  section,
  field,
  useLi,
  degreeOptions,
}: {
  row: RowDiff;
  section: CollectionKey;
  field: RowFieldDiff;
  useLi: boolean;
  degreeOptions: readonly FieldOption[];
}) {
  const intl = useIntl();
  const label = fieldLabel(intl, field.field);
  const fieldId = `profile-merge-row-${row.rowKey}-${field.field}`;
  const isConflict = row.action === "CONFLICT";
  const languageChoice = languageNeedsChoice(field);

  if (!field.editable && !languageChoice) {
    return (
      <div className="min-w-0">
        <span className="smycles-field-meta text-ink-muted">{label}</span>
        <div className="smycles-field-value break-words">
          {machineDisplay(field, field.incomingValue, intl, degreeOptions) ?? "–"}
        </div>
      </div>
    );
  }

  // The chooser replaces the whole field, so the existing side stays out of it: only
  // the INCOMING value is correctable. (genesis matches a language row BY its tag, so
  // a CONFLICT here already carries a resolved one — this keeps a saved tag from
  // turning into a free-text box should that ever stop holding.)
  const showExisting =
    isConflict && !languageChoice && field.existingValue != null && field.existingValue !== "";

  // On a CONFLICT both sides are editable in place (mirroring the person_detail
  // pair): the user can take the LinkedIn value, keep their own, OR edit either.
  // The discarded side (per the current choice) dims. For an ADD there is no
  // existing value, so only the incoming side shows.
  return (
    <div className="min-w-0">
      <label htmlFor={fieldId} className="smycles-field-meta text-ink-muted">
        {label}
      </label>
      {showExisting ? (
        <div
          className={useLi ? "opacity-45" : ""}
          id={`profile-merge-row-existing-${row.rowKey}-${field.field}`}
        >
          <RowFieldEditor
            row={row}
            section={section}
            field={field}
            fieldId={`${fieldId}-existing`}
            side="existing"
            committed={!useLi}
            degreeOptions={degreeOptions}
          />
        </div>
      ) : null}
      <div className={useLi ? "" : "opacity-45"}>
        <RowFieldEditor
          row={row}
          section={section}
          field={field}
          fieldId={fieldId}
          side="incoming"
          committed={useLi}
          degreeOptions={degreeOptions}
        />
      </div>
    </div>
  );
}

function RowCard({
  row,
  section,
  degreeOptions,
}: {
  row: RowDiff;
  section: CollectionKey;
  degreeOptions: readonly FieldOption[];
}) {
  const choices = useProfileMergeStore((s) => s.choices);
  const rowEdits = useProfileMergeStore((s) => s.rowEdits);
  const { activationProps } = useGuideCard(row.rowKey);

  const isConflict = row.action === "CONFLICT";
  const choice: ResolutionChoice = choices[row.rowKey] ?? defaultChoice(row.action);
  const useLi = choice === "USE_INCOMING";
  // A replace-resolved CONFLICT row overwrites the matched saved entry (which
  // may have come from anywhere). Keep that warning textual — never a red or
  // accent border (neutral-state-signals rule, UI_CONVENTIONS "Danger").
  const replacesSaved = rowReplacesSaved(row, choices);

  return (
    <div
      {...activationProps}
      id={`profile-merge-card-${row.rowKey}`}
      className="flex flex-col gap-2 rounded-md border border-ink/10 bg-ink/[0.02] px-3 py-2"
    >
      <div className="flex items-center gap-2">
        <span className="smycles-field-meta rounded px-1.5 py-0.5 text-ink ring-1 ring-ink/15">
          {isConflict ? (
            <FormattedMessage id="profile.merge.badge.conflict" defaultMessage="Differs" />
          ) : (
            <FormattedMessage id="profile.merge.badge.new" defaultMessage="New" />
          )}
        </span>
        <span className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="profile.merge.incomingLabel" defaultMessage="Imported" />
        </span>
        {replacesSaved ? (
          <span id={`profile-merge-replacesrow-${row.rowKey}`} className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="profile.merge.replacesSavedRow" defaultMessage="Replaces your saved entry" />
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        {row.fields.map((field) => (
          <RowFieldRow
            key={field.field}
            row={row}
            section={section}
            field={field}
            useLi={useLi}
            degreeOptions={degreeOptions}
          />
        ))}
      </div>

      <MergeGuideControls
        stepKey={row.rowKey}
        action={row.action}
        useIncomingDisabled={rowTakeBlockedByUnknownLanguage(row, rowEdits)}
      />
    </div>
  );
}

export function ProfileMergeCollection({
  section,
  degreeOptions = [],
}: {
  section: CollectionKey;
  degreeOptions?: readonly FieldOption[];
}) {
  const preview = useProfileMergeStore((s) => s.preview);

  const rows = (preview?.[section] ?? []).filter((r) => r.action !== "UNCHANGED");
  if (rows.length === 0) return null;

  return (
    <div id={`profile-merge-collection-${section}`} className="mt-2 flex flex-col gap-2">
      {rows.map((row) => (
        <RowCard key={row.rowKey} row={row} section={section} degreeOptions={degreeOptions} />
      ))}
    </div>
  );
}
